import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Coupon } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import DeleteConfirmModal from '../../components/admin/DeleteConfirmModal';
import './AdminCoupons.css';

interface CouponFormData {
  code: string;
  type: 'Percentage' | 'FixedAmount';
  value: string;
  minimumPurchase: string;
  maximumDiscount: string;
  expiresAt: string;
  startsAt: string;
  usageLimit: string;
  usageLimitPerCustomer: string;
  isActive: boolean;
}

const AdminCoupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();
  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    type: 'Percentage',
    value: '',
    minimumPurchase: '',
    maximumDiscount: '',
    expiresAt: '',
    startsAt: '',
    usageLimit: '',
    usageLimitPerCustomer: '',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async (): Promise<void> => {
    try {
      const response = await api.get<Coupon[]>('/admin/coupons');
      setCoupons(response.data);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!formData.code.trim()) {
      warning('Coupon code is required.');
      return;
    }
    if (!formData.value || parseFloat(formData.value) <= 0) {
      warning('Coupon value must be greater than 0.');
      return;
    }
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        value: parseFloat(formData.value),
        minimumPurchase: formData.minimumPurchase ? parseFloat(formData.minimumPurchase) : null,
        maximumDiscount: formData.maximumDiscount ? parseFloat(formData.maximumDiscount) : null,
        expiresAt: formData.expiresAt || null,
        startsAt: formData.startsAt || null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        usageLimitPerCustomer: formData.usageLimitPerCustomer ? parseInt(formData.usageLimitPerCustomer) : null,
      };

      if (editingCoupon) {
        await api.put(`/admin/coupons/${editingCoupon.id}`, submitData);
        success('Coupon updated.');
      } else {
        await api.post('/admin/coupons', submitData);
        success('Coupon created.');
      }
      
      fetchCoupons();
      resetForm();
    } catch (error: any) {
      console.error('Failed to save coupon:', error);
      showError('Failed to save coupon: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coupon: Coupon): void => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type as 'Percentage' | 'FixedAmount',
      value: coupon.value.toString(),
      minimumPurchase: coupon.minimumPurchase?.toString() || '',
      maximumDiscount: coupon.maximumDiscount?.toString() || '',
      expiresAt: coupon.expiresAt ? coupon.expiresAt.split('T')[0] : '',
      startsAt: coupon.startsAt ? coupon.startsAt.split('T')[0] : '',
      usageLimit: coupon.usageLimit?.toString() || '',
      usageLimitPerCustomer: coupon.usageLimitPerCustomer?.toString() || '',
      isActive: coupon.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (): Promise<void> => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/coupons/${confirmDeleteId}`);
      fetchCoupons();
      success('Coupon deleted.');
    } catch (error: any) {
      console.error('Failed to delete coupon:', error);
      showError('Failed to delete coupon: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const resetForm = (): void => {
    setFormData({
      code: '',
      type: 'Percentage',
      value: '',
      minimumPurchase: '',
      maximumDiscount: '',
      expiresAt: '',
      startsAt: '',
      usageLimit: '',
      usageLimitPerCustomer: '',
      isActive: true
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-loading">Loading coupons...</div>;

  return (
    <div className="admin-coupons">
      <div className="admin-header">
        <h1>Coupon Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Add New Coupon'}
        </button>
      </div>

      {showForm && (
        <div className="admin-form">
          <h2>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                required
                placeholder="SAVE20"
              />
            </div>

            <div className="form-group">
              <label>Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'Percentage' | 'FixedAmount' })}
                required
              >
                <option value="Percentage">Percentage</option>
                <option value="FixedAmount">Fixed Amount</option>
              </select>
            </div>

            <div className="form-group">
              <label>Value *</label>
              <input
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                required
                placeholder={formData.type === 'Percentage' ? '20 (for 20%)' : '10.00'}
              />
              <small>{formData.type === 'Percentage' ? 'Enter percentage (0-100)' : 'Enter fixed amount'}</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Minimum Purchase</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.minimumPurchase}
                  onChange={(e) => setFormData({ ...formData, minimumPurchase: e.target.value })}
                  placeholder="50.00"
                />
              </div>

              {formData.type === 'Percentage' && (
                <div className="form-group">
                  <label>Maximum Discount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.maximumDiscount}
                    onChange={(e) => setFormData({ ...formData, maximumDiscount: e.target.value })}
                    placeholder="25.00"
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Starts At</label>
                <input
                  type="date"
                  value={formData.startsAt}
                  onChange={(e) => setFormData({ ...formData, startsAt: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Expires At</label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Usage Limit (Total)</label>
                <input
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                  placeholder="100"
                />
              </div>

              <div className="form-group">
                <label>Usage Limit Per Customer</label>
                <input
                  type="number"
                  value={formData.usageLimitPerCustomer}
                  onChange={(e) => setFormData({ ...formData, usageLimitPerCustomer: e.target.value })}
                  placeholder="1"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                {editingCoupon ? 'Update' : 'Create'} Coupon
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Value</th>
              <th>Usage</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr>
                <td colSpan={7}>No coupons found</td>
              </tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td><strong>{coupon.code}</strong></td>
                  <td>{coupon.type}</td>
                  <td>
                    {coupon.type === 'Percentage' 
                      ? `${coupon.value}%` 
                      : `$${coupon.value.toFixed(2)}`}
                  </td>
                  <td>
                    {coupon.usageCount} / {coupon.usageLimit || '∞'}
                  </td>
                  <td>
                    <span className={`badge ${coupon.isValid ? 'badge-success' : 'badge-danger'}`}>
                      {coupon.isValid ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{coupon.expiresAt ? new Date(coupon.expiresAt).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <button className="btn btn-sm btn-primary" onClick={() => handleEdit(coupon)}>
                      Edit
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => setConfirmDeleteId(coupon.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <DeleteConfirmModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        itemName={confirmDeleteId ? coupons.find(c => c.id === confirmDeleteId)?.code : undefined}
      />
    </div>
  );
};

export default AdminCoupons;
